import React from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";
import i18n from "src/i18n/config";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch } from "src/store";
import { selectCurrentWorkspaceId, setCurrentWorkspaceId } from "src/store/app";

import { Product } from "@dashboard/products/types/index";
import { useAllWorkspaces, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { getDefaultProduct } from "@products/utils/getDefaultProduct/getDefaultProduct";
import { showErrorToast } from "@shared/toasts/Toasts";

import { useCurrentWorkspaceProducts } from "./api/helpers";

type ProductId = number | null;
type UseCurrentProduct = (products: Product[]) => [ProductId, (value: ProductId) => void];

export const useCurrentProduct: UseCurrentProduct = (products) => {
    const defaultProduct = getDefaultProduct(products);
    const [id, setId] = React.useState<ProductId>(defaultProduct?.id ?? null);

    const setCurrentProductId = (value: number | null) => {
        setId(value);
        UserSettingsService.setWorkspaceValue("productPage.lastSelectedProduct", value);
    };

    React.useEffect(() => {
        if (id !== defaultProduct?.id && id) {
            UserSettingsService.setWorkspaceValue("productPage.lastSelectedProduct", id);
        }
    }, [id, defaultProduct?.id]);

    return [id, setCurrentProductId];
};

function getProduct(products: Product[], codename: string | undefined, androidVersion: string | undefined) {
    return products.find((product) => product.codename === codename && product.androidVersion === androidVersion);
}

type WorkspaceTarget = number | null;

function resolveWorkspaceTarget(
    searchParams: URLSearchParams,
    workspaces: API.Workspace[],
    currentWorkspace: API.Workspace | null,
): WorkspaceTarget {
    const guid = searchParams.get("workspace");
    const workspaceId = Number(searchParams.get("workspaceId"));

    const sameWorkspace = currentWorkspace?.guid === guid || currentWorkspace?.id === workspaceId;
    if (sameWorkspace) {
        return currentWorkspace.id;
    }

    const newWorkspace = workspaces.find((item) => item.guid === guid || item.id === workspaceId);
    if (newWorkspace) {
        return newWorkspace.id;
    }

    const noTargetGiven = !guid && (!Number.isInteger(workspaceId) || workspaceId <= 0);
    if (noTargetGiven) {
        return null;
    }

    return -1;
}

type Props = {
    setCurrentProductId: (id: number) => void;
};
export const useChangeProductFromUrl = ({ setCurrentProductId }: Props) => {
    const { data: products = [], isFetching: isFetchingProducts, isSuccess: hasProducts } = useCurrentWorkspaceProducts();
    const { data: workspaces = [], isFetching: isFetchingWorkspaces, isSuccess: hasWorkspaces } = useAllWorkspaces();
    const { data: loadedWorkspace } = useCurrentWorkspace();
    const currentWorkspaceId = useSelector(selectCurrentWorkspaceId);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { codename, androidVersion } = useParams();

    const [searchParams] = useSearchParams();
    const workspaceTarget = resolveWorkspaceTarget(searchParams, workspaces, loadedWorkspace);

    // Applied at most once per mount: once the link has picked a product (or given up and
    // redirected), re-running would pull the user back from whatever they select next.
    const isApplied = React.useRef(false);

    // The products query is keyed by the workspace useCurrentWorkspace resolves, which lags a
    // render behind the store right after a switch. Comparing the two keeps the product lookup
    // off the previous workspace's list, which is what reported "product not found" on a link
    // pointing at another workspace. The workspace list gates the access check for the same
    // reason: while it is still empty every target looks -1.
    const hasProductsForCurrentWorkspace = hasProducts && !isFetchingProducts && loadedWorkspace?.id === currentWorkspaceId;
    const isDataReady = hasProductsForCurrentWorkspace && hasWorkspaces && !isFetchingWorkspaces;

    React.useEffect(() => {
        if (isApplied.current || !isDataReady) {
            return;
        }

        try {
            const workspaceTargetIsInaccessible = workspaceTarget === -1;
            if (workspaceTargetIsInaccessible) {
                isApplied.current = true;
                showErrorToast(i18n.t("pages:productPage.toast.donHaveAccessToWorkspace"));
                void navigate("/dashboard/products", { replace: true });
                return;
            }

            const differentWorkspace = workspaceTarget !== null && workspaceTarget !== currentWorkspaceId;
            if (differentWorkspace) {
                dispatch(setCurrentWorkspaceId(workspaceTarget));
                return;
            }

            const noProductGiven = !codename || !androidVersion;
            if (noProductGiven) {
                return;
            }

            isApplied.current = true;

            const product = getProduct(products, codename, androidVersion);
            if (!product) {
                showErrorToast(i18n.t("pages:productPage.toast.donHaveAccessToProduct"));
                void navigate("/dashboard/products", { replace: true });
                return;
            }

            setCurrentProductId(product.id);
        } catch (error) {
            // The app has no error boundary, so a throw from this effect would blank the page.
            // eslint-disable-next-line no-console
            console.error("Failed to apply the product deep link", error);
        }
    }, [
        isDataReady,
        products,
        workspaceTarget,
        currentWorkspaceId,
        codename,
        androidVersion,
        dispatch,
        navigate,
        setCurrentProductId,
    ]);
};
